import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, fecha, hoy } from '../utils/formato';

// Los contratos.
//
// Un contrato une un inmueble con un arrendatario durante un tiempo. Solo puede
// haber uno activo por inmueble: dos vigentes sobre el mismo local significan
// que alguien se equivocó al elegirlo, y si se deja pasar, el cobro y la
// vacancia quedan mal para siempre. Eso lo comprueba el servidor.
//
// Terminar NO borra. De un contrato cuelgan los pagos y la historia del
// inmueble, y esa historia es la que después dice si el local rinde.

const VACIO = {
  inmueble_id: '', arrendatario_id: '', fecha_inicio: hoy(), fecha_fin: '',
  canon: '', canon_primer_mes: '', dia_pago: 5, incremento_pct: '', deposito: '',
  paga_servicios: 'arrendatario', paga_administracion: 'arrendatario',
  paga_predial: 'propietario',
  codeudor_nombre: '', codeudor_documento: '', codeudor_telefono: '',
  codeudor_direccion: '', nota: '',
};

const QUIEN = [
  { id: 'arrendatario', nombre: 'El arrendatario' },
  { id: 'propietario',  nombre: 'Yo (propietario)' },
];

function Contratos({ onCambio }) {
  const [contratos, setContratos] = useState([]);
  const [inmuebles, setInmuebles] = useState([]);
  const [arrendatarios, setArrendatarios] = useState([]);
  const [ajustes, setAjustes] = useState({ tope_incremento_vivienda: 0 });

  const [nuevo, setNuevo] = useState(null);
  const [conCodeudor, setConCodeudor] = useState(false);
  const [terminando, setTerminando] = useState(null);
  const [verTerminados, setVerTerminados] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => {
    axios.get('/api/contratos').then(r => setContratos(r.data || [])).catch(() => {});
    axios.get('/api/inmuebles/estado').then(r => setInmuebles(r.data || [])).catch(() => {});
    axios.get('/api/arrendatarios').then(r => setArrendatarios(r.data || [])).catch(() => {});
    axios.get('/api/ajustes').then(r => setAjustes(r.data || {})).catch(() => {});
  }, []);

  useEffect(cargar, [cargar]);

  const libres = inmuebles.filter(i => i.estado === 'vacio');
  const elegido = inmuebles.find(i => String(i.id) === String(nuevo?.inmueble_id));
  const esVivienda = elegido?.tipo === 'vivienda';
  const tope = parseFloat(ajustes.tope_incremento_vivienda) || 0;
  const sePasaDelTope = esVivienda && tope > 0
    && parseFloat(nuevo?.incremento_pct) > tope;

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const datos = { ...nuevo };
      if (!conCodeudor) {
        datos.codeudor_nombre = '';
        datos.codeudor_documento = '';
        datos.codeudor_telefono = '';
        datos.codeudor_direccion = '';
      }
      await axios.post('/api/contratos', datos);
      setAviso({ tipo: 'bueno', texto: 'Contrato creado' });
      setNuevo(null);
      setConCodeudor(false);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo crear el contrato' });
    }
    setGuardando(false);
  };

  const terminar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const { data } = await axios.post(`/api/contratos/${terminando.contrato.id}/terminar`, {
        fecha_terminacion: terminando.fecha,
        motivo_terminacion: terminando.motivo || null,
        deposito_devuelto: parseFloat(terminando.devuelto) || 0,
      });
      const q = data.estado_cuenta.saldo;
      setAviso({
        tipo: q > 0 ? 'ojo' : 'bueno',
        texto: q > 0
          ? `Contrato terminado, pero queda debiendo ${plata(q)}. Sigue apareciendo en Cobrar.`
          : 'Contrato terminado y sin deuda pendiente.',
      });
      setTerminando(null);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo terminar' });
    }
    setGuardando(false);
  };

  const activos = contratos.filter(c => c.estado === 'activo');
  const terminados = contratos.filter(c => c.estado === 'terminado');
  const visibles = verTerminados ? terminados : activos;

  const diasPara = (f) => f
    ? Math.floor((Date.parse(String(f).slice(0, 10)) - Date.parse(hoy())) / 86400000)
    : null;

  return (
    <>
      <div className="encabezado">
        <h1>Contratos</h1>
        <p>Quién ocupa qué, por cuánto y hasta cuándo</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      <div className="grupo">
        <button className={'opcion' + (!verTerminados ? ' activa' : '')}
                onClick={() => setVerTerminados(false)}>
          📄 Activos ({activos.length})
        </button>
        <button className={'opcion' + (verTerminados ? ' activa' : '')}
                onClick={() => setVerTerminados(true)}>
          📕 Terminados ({terminados.length})
        </button>
      </div>

      <div className="bloque">
        <h2>{verTerminados ? '📕 Terminados' : '📄 Contratos activos'}
          <span className="contador">{visibles.length}</span>
        </h2>
        <div className="cuerpo">
          {!verTerminados && (
            <div className="acciones" style={{ marginBottom: 14 }}>
              <button className="boton" disabled={libres.length === 0 || arrendatarios.length === 0}
                      onClick={() => setNuevo({ ...VACIO })}>
                ➕ Nuevo contrato
              </button>
            </div>
          )}

          {!verTerminados && libres.length === 0 && inmuebles.length > 0 && (
            <div className="aviso">
              Todos los inmuebles están arrendados. Para hacer un contrato nuevo hay
              que terminar uno antes, o crear otro inmueble.
            </div>
          )}
          {arrendatarios.length === 0 && (
            <div className="aviso ojo">
              No hay arrendatarios todavía. Créalos en <strong>Arrendatarios</strong>
              {' '}antes de hacer el contrato.
            </div>
          )}

          {visibles.length === 0 ? (
            <div className="vacio">
              {verTerminados ? 'Ningún contrato terminado todavía.'
                             : 'No hay contratos activos.'}
            </div>
          ) : (
            <div className="tabla-caja">
              <table className="datos">
                <thead>
                  <tr>
                    <th>Inmueble</th>
                    <th>Arrendatario</th>
                    <th style={{ textAlign: 'right' }}>Canon</th>
                    <th>Desde</th>
                    <th>{verTerminados ? 'Terminó' : 'Hasta'}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map(c => {
                    const dias = diasPara(c.fecha_fin);
                    const e = c.estado_cuenta;
                    return (
                      <tr key={c.id}>
                        <td className="recio">
                          {c.inmueble}
                          <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                            {c.inmueble_tipo}
                          </div>
                        </td>
                        <td>
                          {c.arrendatario}
                          {c.codeudor_nombre && (
                            <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                              codeudor: {c.codeudor_nombre}
                            </div>
                          )}
                        </td>
                        <td className="num recio">
                          {plata(e.canon_vigente)}
                          {c.incremento_pct > 0 && (
                            <div style={{ fontSize: 11, fontWeight: 400,
                                          color: 'var(--tinta-tenue)' }}>
                              +{c.incremento_pct}% al año
                            </div>
                          )}
                        </td>
                        <td style={{ fontSize: 13 }}>{fecha(c.fecha_inicio)}</td>
                        <td style={{ fontSize: 13 }}>
                          {verTerminados ? (
                            <>
                              {fecha(c.fecha_terminacion)}
                              {c.motivo_terminacion && (
                                <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                  {c.motivo_terminacion}
                                </div>
                              )}
                            </>
                          ) : c.fecha_fin ? (
                            <>
                              <span className={'pinta ' + (dias == null ? 'gris'
                                : dias < 0 ? 'malo' : dias <= 90 ? 'ojo' : 'bueno')}>
                                {dias < 0 ? `vencido hace ${-dias} d`
                                  : `en ${dias} d`}
                              </span>
                              <div style={{ fontSize: 11, color: 'var(--tinta-tenue)', marginTop: 3 }}>
                                {fecha(c.fecha_fin)}
                              </div>
                            </>
                          ) : <span className="pinta gris">indefinido</span>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {e.saldo > 0 && (
                            <span className="pinta malo" style={{ marginRight: 6 }}>
                              debe {plata(e.saldo)}
                            </span>
                          )}
                          {!verTerminados && (
                            <button className="boton chico suave"
                                    onClick={() => setTerminando({
                                      contrato: c, fecha: hoy(), motivo: '',
                                      devuelto: String(c.deposito || 0),
                                    })}>
                              Terminar
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Nuevo contrato ────────────────────────────────────────────────── */}
      {nuevo && (
        <div className="bloque">
          <h2>➕ Nuevo contrato</h2>
          <form onSubmit={guardar}>
            <div className="cuerpo">
              <div className="campos">
                <label className="campo">
                  <span>Inmueble *</span>
                  <select value={nuevo.inmueble_id} required
                          onChange={e => setNuevo({ ...nuevo, inmueble_id: e.target.value })}>
                    <option value="">— Elegir —</option>
                    {libres.map(i => (
                      <option key={i.id} value={i.id}>{i.nombre} ({i.tipo})</option>
                    ))}
                  </select>
                </label>
                <label className="campo">
                  <span>Arrendatario *</span>
                  <select value={nuevo.arrendatario_id} required
                          onChange={e => setNuevo({ ...nuevo, arrendatario_id: e.target.value })}>
                    <option value="">— Elegir —</option>
                    {arrendatarios.map(a => (
                      <option key={a.id} value={a.id}>{a.nombre}</option>
                    ))}
                  </select>
                </label>
                <label className="campo">
                  <span>Empieza *</span>
                  <input type="date" required value={nuevo.fecha_inicio}
                         onChange={e => setNuevo({ ...nuevo, fecha_inicio: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Se vence</span>
                  <input type="date" value={nuevo.fecha_fin}
                         onChange={e => setNuevo({ ...nuevo, fecha_fin: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Canon mensual *</span>
                  <input type="number" step="1" min="0" className="num-grande" required
                         value={nuevo.canon}
                         onChange={e => setNuevo({ ...nuevo, canon: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Día de pago</span>
                  <input type="number" step="1" min="1" max="28" value={nuevo.dia_pago}
                         onChange={e => setNuevo({ ...nuevo, dia_pago: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Sube cada año (%)</span>
                  <input type="number" step="0.1" min="0" value={nuevo.incremento_pct}
                         placeholder="0"
                         onChange={e => setNuevo({ ...nuevo, incremento_pct: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Depósito</span>
                  <input type="number" step="1" min="0" value={nuevo.deposito} placeholder="0"
                         onChange={e => setNuevo({ ...nuevo, deposito: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Primer mes, si es a medias</span>
                  <input type="number" step="1" min="0" value={nuevo.canon_primer_mes}
                         placeholder="completo"
                         onChange={e => setNuevo({ ...nuevo, canon_primer_mes: e.target.value })} />
                </label>
              </div>

              {sePasaDelTope && (
                <div className="aviso malo" style={{ marginTop: 12 }}>
                  <strong>Ojo:</strong> es vivienda y el incremento que puso
                  ({nuevo.incremento_pct}%) está por encima del tope que usted
                  configuró ({tope}%). En Colombia el aumento de vivienda tiene
                  límite legal. Compruébelo antes de firmar.
                </div>
              )}

              {nuevo.canon_primer_mes && (
                <div className="aviso" style={{ marginTop: 12 }}>
                  El primer mes se cobrará <strong>{plata(nuevo.canon_primer_mes)}</strong>{' '}
                  y desde el segundo, <strong>{plata(nuevo.canon)}</strong>. Sin esto,
                  un primer mes prorrateado deja una deuda que nunca se salda.
                </div>
              )}

              <h3 style={{ fontSize: 15, marginTop: 22 }}>Quién paga qué</h3>
              <div className="campos">
                <label className="campo">
                  <span>Servicios</span>
                  <select value={nuevo.paga_servicios}
                          onChange={e => setNuevo({ ...nuevo, paga_servicios: e.target.value })}>
                    {QUIEN.map(q => <option key={q.id} value={q.id}>{q.nombre}</option>)}
                  </select>
                </label>
                <label className="campo">
                  <span>Administración</span>
                  <select value={nuevo.paga_administracion}
                          onChange={e => setNuevo({ ...nuevo, paga_administracion: e.target.value })}>
                    {QUIEN.map(q => <option key={q.id} value={q.id}>{q.nombre}</option>)}
                  </select>
                </label>
                <label className="campo">
                  <span>Predial</span>
                  <select value={nuevo.paga_predial}
                          onChange={e => setNuevo({ ...nuevo, paga_predial: e.target.value })}>
                    {QUIEN.map(q => <option key={q.id} value={q.id}>{q.nombre}</option>)}
                  </select>
                </label>
              </div>

              <div className="acciones" style={{ marginTop: 18, marginBottom: 0 }}>
                <button type="button" className="boton suave chico"
                        onClick={() => setConCodeudor(!conCodeudor)}>
                  {conCodeudor ? '− Quitar codeudor' : '➕ Agregar codeudor'}
                </button>
              </div>

              {conCodeudor && (
                <div className="campos" style={{ marginTop: 12 }}>
                  <label className="campo">
                    <span>Nombre del codeudor</span>
                    <input value={nuevo.codeudor_nombre}
                           onChange={e => setNuevo({ ...nuevo, codeudor_nombre: e.target.value })} />
                  </label>
                  <label className="campo">
                    <span>Cédula</span>
                    <input value={nuevo.codeudor_documento}
                           onChange={e => setNuevo({ ...nuevo, codeudor_documento: e.target.value })} />
                  </label>
                  <label className="campo">
                    <span>Teléfono</span>
                    <input value={nuevo.codeudor_telefono}
                           onChange={e => setNuevo({ ...nuevo, codeudor_telefono: e.target.value })} />
                  </label>
                  <label className="campo">
                    <span>Dirección</span>
                    <input value={nuevo.codeudor_direccion}
                           onChange={e => setNuevo({ ...nuevo, codeudor_direccion: e.target.value })} />
                  </label>
                </div>
              )}

              <div className="acciones">
                <button type="button" className="boton suave"
                        onClick={() => { setNuevo(null); setConCodeudor(false); }}>
                  Cancelar
                </button>
                <button className="boton" disabled={guardando}>
                  {guardando ? 'Guardando…' : '💾 Crear contrato'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ── Terminar ──────────────────────────────────────────────────────── */}
      {terminando && (
        <div className="bloque">
          <h2>Terminar el contrato de {terminando.contrato.inmueble}</h2>
          <form onSubmit={terminar}>
            <div className="cuerpo">
              {terminando.contrato.estado_cuenta.saldo > 0 && (
                <div className="aviso ojo" style={{ marginBottom: 16 }}>
                  Queda debiendo{' '}
                  <strong>{plata(terminando.contrato.estado_cuenta.saldo)}</strong>.
                  Terminar el contrato <strong>no borra la deuda</strong>: sigue
                  apareciendo para cobrar.
                </div>
              )}

              <div className="campos">
                <label className="campo">
                  <span>Fecha de terminación</span>
                  <input type="date" value={terminando.fecha}
                         onChange={e => setTerminando({ ...terminando, fecha: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Depósito que se devuelve</span>
                  <input type="number" step="1" min="0" value={terminando.devuelto}
                         onChange={e => setTerminando({ ...terminando, devuelto: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Por qué se termina</span>
                  <input value={terminando.motivo} placeholder="Se trasladó, no renovó…"
                         onChange={e => setTerminando({ ...terminando, motivo: e.target.value })} />
                </label>
              </div>

              {terminando.contrato.deposito > 0 && (
                <div style={{ marginTop: 12, fontSize: 14 }}>
                  Recibió {plata(terminando.contrato.deposito)} de depósito. Devolviendo{' '}
                  {plata(parseFloat(terminando.devuelto) || 0)}, se queda con{' '}
                  <strong>
                    {plata(terminando.contrato.deposito - (parseFloat(terminando.devuelto) || 0))}
                  </strong>.
                </div>
              )}

              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setTerminando(null)}>
                  Cancelar
                </button>
                <button className="boton peligro" disabled={guardando}>
                  {guardando ? 'Guardando…' : 'Terminar contrato'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default Contratos;
